import { useState, lazy, Suspense } from "react";
import { Mail, ArrowUpRight, Instagram, Facebook } from "lucide-react";
import { useTranslation } from "react-i18next";

const AuthModal = lazy(() => import("../auth/AuthModal"));

const Footer = () => {
  const year = new Date().getFullYear();
  const { t } = useTranslation('nav');
  const [authModal, setAuthModal] = useState({ open: false, mode: 'login' });

  const platformLinks = [
    { href: "/attract-clients", label: t('attract_clients') },
    { href: "/calendar", label: t('calendar') },
    { href: "/website", label: t('website') },
    { href: "/sitemap", label: t('sitemap') },
  ];

  const accountLinks = [
    { href: "/terms", label: t('terms') },
    { href: "/privacy", label: t('privacy') },
    { href: "/booking-rules", label: t('booking_rules') },
    { href: "/content-policy", label: t('content_policy') },
  ];

  return (
    <footer style={{ background: "var(--color-ink-900)", position: "relative", overflow: "hidden" }}>
      <style>{`
        .bk-footer-grid {
          display: grid;
          grid-template-columns: 1.2fr 1.6fr 1fr 1fr 1.4fr;
          gap: 40px;
          align-items: start;
        }
        .bk-footer-logo-col { display: flex; flex-direction: column; align-items: flex-start; }
          .bk-footer-logo-mobile { display: none; }
        @media (max-width: 1000px) {
          .bk-footer-grid { grid-template-columns: 1fr 1fr; gap: 36px; }
          .bk-footer-logo-col { display: none; }
          .bk-footer-logo-mobile { display: block; height: 180px; margin-left: auto; margin-right: auto; }
        }
        @media (max-width: 560px) {
          .bk-footer-grid { grid-template-columns: 1fr; gap: 28px; }
        }
      `}</style>

      {/* Ambient blobs */}
      <div style={{
        position: "absolute", top: "-100px", left: "-80px",
        width: "420px", height: "420px", borderRadius: "50%",
        background: "radial-gradient(circle, rgba(139,111,212,0.12) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: "-80px", right: "-60px",
        width: "360px", height: "360px", borderRadius: "50%",
        background: "radial-gradient(circle, rgba(244,149,111,0.08) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />

      {/* Top divider */}
      <div style={{
        height: "1px",
        background: "linear-gradient(90deg, transparent 0%, rgba(139,111,212,0.40) 30%, rgba(244,149,111,0.35) 70%, transparent 100%)",
      }} />

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "60px 24px 0", position: "relative" }}>
        <div className="bk-footer-grid">

          {/* Col 1 — Logo only (desktop) */}
          <div className="bk-footer-logo-col">
            <img
              src="/logo.png"
              alt="Salonite365"
              loading="lazy"
              decoding="async"
              width={1024}
              height={1024}
              style={{
                width: "100%",
                maxWidth: "200px",
                height: "auto",
                objectFit: "contain",
                filter: "brightness(1.15) drop-shadow(0 0 14px rgba(212,175,55,0.4))",
              }}
            />
          </div>

          {/* Col 2 — Brand info */}
          <div>
            {/* Logo — mobile only (col 1 is hidden on mobile) */}
            <img
              src="/logo.png"
              alt="Salonite365"
              loading="lazy"
              decoding="async"
              width={1024}
              height={1024}
              style={{
                width: "auto",
                objectFit: "contain",
                marginBottom: "16px",
                filter: "brightness(1.15) drop-shadow(0 0 14px rgba(212,175,55,0.4))",
              }}
              className="bk-footer-logo-mobile"
            />
            <div style={{ marginBottom: "20px" }}>
              <span style={{
                fontSize: "0.68rem", letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(167,139,220,0.55)",
                display: "block", marginBottom: "10px",
              }}>
                {t('tagline')}
              </span>
            </div>

            <p style={{
              fontSize: "0.84rem", lineHeight: "1.75",
              color: "rgba(200,212,248,0.75)",
              maxWidth: "260px", marginBottom: "22px",
            }}>
              {t('description')}
            </p>

            <a
              href="mailto:support@salonite365.bg"
              style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                fontSize: "0.83rem", color: "rgba(167,139,220,0.80)",
                textDecoration: "none", transition: "color 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.color = "#c9b7f0"}
              onMouseLeave={e => e.currentTarget.style.color = "rgba(167,139,220,0.80)"}
            >
              <Mail size={13} />
              support@salonite365.bg
            </a>
          </div>

          {/* Col 3 — Platform links */}
          <div>
            <p style={{
              fontSize: "0.65rem", fontWeight: "700",
              letterSpacing: "0.16em", textTransform: "uppercase",
              color: "rgba(167,139,220,0.50)",
              marginBottom: "16px",
            }}>
              {t('platform')}
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {platformLinks.map(link => (
                <li key={link.href} style={{ marginBottom: "11px" }}>
                  <a
                    href={link.href}
                    style={{
                      fontSize: "0.86rem",
                      color: "rgba(200,212,248,0.75)",
                      textDecoration: "none", transition: "color 0.15s", display: "block",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "rgba(200,212,248,0.95)"}
                    onMouseLeave={e => e.currentTarget.style.color = "rgba(200,212,248,0.75)"}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — Account links */}
          <div>
            <p style={{
              fontSize: "0.65rem", fontWeight: "700",
              letterSpacing: "0.16em", textTransform: "uppercase",
              color: "rgba(167,139,220,0.50)",
              marginBottom: "16px",
            }}>
              {t('account')}
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {[
                { modal: 'login', label: t('login') },
                { modal: 'register', label: t('register') },
              ].map(item => (
                <li key={item.modal} style={{ marginBottom: "11px" }}>
                  <button
                    onClick={() => setAuthModal({ open: true, mode: item.modal })}
                    style={{
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                      fontSize: "0.86rem",
                      color: "rgba(200,212,248,0.75)",
                      textDecoration: "none", transition: "color 0.15s", display: "block",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "rgba(200,212,248,0.95)"}
                    onMouseLeave={e => e.currentTarget.style.color = "rgba(200,212,248,0.75)"}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
              {accountLinks.map(link => (
                <li key={link.href} style={{ marginBottom: "11px" }}>
                  <a
                    href={link.href}
                    style={{
                      fontSize: "0.86rem",
                      color: "rgba(200,212,248,0.75)",
                      textDecoration: "none", transition: "color 0.15s", display: "block",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "rgba(200,212,248,0.95)"}
                    onMouseLeave={e => e.currentTarget.style.color = "rgba(200,212,248,0.75)"}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {authModal.open && (
            <Suspense fallback={null}>
              <AuthModal
                isOpen={authModal.open}
                onClose={() => setAuthModal(prev => ({ ...prev, open: false }))}
                initialMode={authModal.mode}
              />
            </Suspense>
          )}

          {/* Col 5 — CTA */}
          <div>
            <p style={{
              fontSize: "0.65rem", fontWeight: "700",
              letterSpacing: "0.16em", textTransform: "uppercase",
              color: "rgba(167,139,220,0.50)",
              marginBottom: "16px",
            }}>
              {t('for_salons_footer')}
            </p>

            <a
              href="/addsalon"
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 18px", borderRadius: "var(--radius-md)",
                background: "rgba(139,111,212,0.12)",
                border: "1px solid rgba(139,111,212,0.26)",
                color: "rgba(199,183,240,0.85)",
                textDecoration: "none", fontSize: "0.88rem", fontWeight: "600",
                marginBottom: "10px", transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(139,111,212,0.20)"; e.currentTarget.style.borderColor = "rgba(139,111,212,0.44)"; e.currentTarget.style.color = "#c9b7f0"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(139,111,212,0.12)"; e.currentTarget.style.borderColor = "rgba(139,111,212,0.26)"; e.currentTarget.style.color = "rgba(199,183,240,0.85)"; }}
            >
              {t('add_salon')} <ArrowUpRight size={15} />
            </a>

            <a
              href="/addsalon"
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 18px", borderRadius: "var(--radius-md)",
                background: "rgba(244,149,111,0.12)",
                border: "1px solid rgba(244,149,111,0.26)",
                color: "rgba(247,185,161,0.88)",
                textDecoration: "none", fontSize: "0.88rem", fontWeight: "600",
                marginBottom: "22px", transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(244,149,111,0.20)"; e.currentTarget.style.borderColor = "rgba(244,149,111,0.44)"; e.currentTarget.style.color = "#f7b9a1"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(244,149,111,0.12)"; e.currentTarget.style.borderColor = "rgba(244,149,111,0.26)"; e.currentTarget.style.color = "rgba(247,185,161,0.88)"; }}
            >
              {t('mobile_app')} <ArrowUpRight size={15} />
            </a>

            {/* App badges */}
            <div style={{ display: "flex", gap: "8px", flexWrap: "nowrap", alignItems: "center" }}>
              {[
                { src: "/emaillogos/googleplay.png", alt: "Google Play", w: "108px", h: "36px" },
                { src: "/emaillogos/applestore.png", alt: "App Store", w: "108px", h: "36px" },
                { src: "/emaillogos/huawei.png", alt: "Huawei", w: "40px", h: "36px" },
              ].map(badge => (
                <a
                  key={badge.alt}
                  href="#"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    height: badge.h, width: badge.w,
                    borderRadius: "var(--radius-sm)", overflow: "hidden",
                    opacity: 0.75, transition: "opacity 0.2s, transform 0.2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = "0.75"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <img
                    src={badge.src}
                    alt={badge.alt}
                    loading="lazy"
                    decoding="async"
                    width={parseInt(badge.w, 10)}
                    height={parseInt(badge.h, 10)}
                    style={{ height: "100%", width: "100%", objectFit: "contain" }}
                  />
                </a>
              ))}
            </div>
          </div>

        </div>

        {/* Bottom bar */}
        <div style={{
          marginTop: "48px", paddingTop: "18px", paddingBottom: "22px",
          borderTop: "1px solid rgba(167,139,220,0.12)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: "10px",
        }}>
          <p style={{ fontSize: "0.75rem", color: "rgba(200,212,248,0.60)", margin: 0 }}>
            © {year} salonite365.bg — {t('rights')}
          </p>
          <div style={{ display: "flex", alignItems: "center" }}>
            {[
              { href: "/privacy", label: t('privacy') },
              { href: "/terms", label: t('terms') },
              { href: "/booking-rules", label: t('booking_rules') },
              { href: "/content-policy", label: t('content_policy') },
              { href: "#cookie-preferences", label: t('cookie_preferences'), onClick: (e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('cookie-consent-open'));
                } },
            ].map((link, i) => (
              <span key={link.href} style={{ display: "flex", alignItems: "center" }}>
                {i > 0 && <span style={{ color: "rgba(167,139,220,0.22)", margin: "0 10px", fontSize: "0.7rem" }}>·</span>}
                <a
                  href={link.href}
                  onClick={link.onClick}
                  style={{ fontSize: "0.75rem", color: "rgba(200,212,248,0.60)", textDecoration: "none", transition: "color 0.15s", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.color = "rgba(200,212,248,0.90)"}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(200,212,248,0.60)"}
                >
                  {link.label}
                </a>
              </span>
            ))}
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;